import { Session } from "@/common/models/session";
import { DBSessionThunks } from "@/common/reducers/DBServiceThunks";
import { WaStateStatus } from "@bt-common/interfaces/message-service-interface";
import { backendApi } from "@common/reducers/backend-api/backend-api";
import { isWithinDebounce } from "@common/noifier-module/reconnect-policy";
import store, { dispatch as appDispatch } from "../../common/store/extension-store";
import { unwrapQueryMessages } from "../components/ai-assistant-button/unwrap-query-messages";
import { ContentMessageServiceActions } from "./contentMessageServiceActions";
import { collectRepliedTargets, ScanMsg } from "./drip-reply-reconcile";
import { AccountHealthReporter } from "./account-health-reporter";
import { deriveWaPairing, type WaPairing } from "@common/noifier-module/wa-pairing";
import { ClientType, getClientType, isWhatsappExtensionContext } from "@common/services/detect-context";

export class SessionStatusAutomaton {
    private static instance: SessionStatusAutomaton | null = null;

    private currentCalcSession: Session | null = {};
    private previusCalcSession: Session | null = {};
    //private sessionHistoryLog: Session[] = [];
    //private readonly MAX_HISTORY = 50;
    private initialQRCodeTimestamp: number | null = null;

    private readonly RELOAD_IF_WA_DISCONNECTED_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
    private readonly QR_CODE_TIMEOUT_MS = 4 * 60 * 1000; // 4 minutes; gateway pods only (#714)
    private readonly LAST_SEEN_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    private readonly STATUS_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes - refresh status to prevent cleanup job
    private waDisconnectCheckTimeout: NodeJS.Timeout | null = null;
    private statusHeartbeatInterval: NodeJS.Timeout | null = null;
    private moreTimeforWaToSyncTrigger: boolean = false;

    // invariant: assigned via setSessionContext() before any use
    private ownerId!: string;
    private sessionId!: string;
    private localSessionId!: string;
    private podId!: string;

    private waLogoutTrigger: boolean = false;

    // Last pairing state published in this client's presence record (undefined = never stated).
    private publishedWaPairing: WaPairing | undefined;

    // Debounce guard for the on-reconnect campaign-stats reconcile. A flapping WA connection
    // (this fleet has documented gateway reconnect storms) would otherwise re-run the full ack
    // recalculation on every reconnect edge. Mirrors MQTTSubscriber.resyncAfterGap, which guards
    // its reconcile with isWithinDebounce(lastResyncAt, now, 15000). epoch-ms of the last reconcile;
    // 0 = never.
    private lastCampaignReconcileAt = 0;
    private readonly CAMPAIGN_RECONCILE_DEBOUNCE_MS = 15 * 1000; // 15s — matches MQTT RESYNC_DEBOUNCE_MS

    // Same reconnect-storm guard for the drip reply-exit catch-up, with its OWN
    // last-fire timestamp (independent of the campaign reconcile so neither
    // debounces the other). Same 15s window + same isWithinDebounce helper.
    private lastDripReplyReconcileAt = 0;
    private readonly DRIP_REPLY_RECONCILE_DEBOUNCE_MS = 15 * 1000; // 15s — matches CAMPAIGN_RECONCILE_DEBOUNCE_MS


    public setSessionContext(ownerId: string, sessionId: string, localSessionId: string, podId: string) {
        this.ownerId = ownerId;
        this.sessionId = sessionId;
        this.localSessionId = localSessionId;
        this.podId = podId;
    }

    public makeSureLocalStorageValuesAreSet() {
        if (!this.sessionId) {
            this.sessionId = window.localStorage.getItem("sessionId") || '';
        }
        if (!this.podId) {
            this.podId = window.localStorage.getItem("podId") || '';
        }
    }

    private resetWaDisconnectTimer(): void {

        if (this.waDisconnectCheckTimeout) {
            clearTimeout(this.waDisconnectCheckTimeout);
        }

        // #764: the reload targets the WhatsApp page. Off it (the web app bundles this class) the
        // status never reaches `connected`, so the timer would fire into a page bridge that isn't there.
        if (!isWhatsappExtensionContext()) {
            this.waDisconnectCheckTimeout = null;
            return;
        }

        this.waDisconnectCheckTimeout = setTimeout(() => {

            console.log('[waStateManager] [SessionStatusAutomaton] (resetWaDisconnectTimer) Reloading page because WhatsApp is disconnected');

            if (this.currentCalcSession?.status !== 'connected') {
                ContentMessageServiceActions.waReloadPageTool(`whatsapp is disconnected for ${this.RELOAD_IF_WA_DISCONNECTED_INTERVAL_MS}ms`);
            }

        }, this.RELOAD_IF_WA_DISCONNECTED_INTERVAL_MS);

    }

    private constructor() {
        this.resetWaDisconnectTimer();
        this.startStatusHeartbeat();
    }

    private startStatusHeartbeat(): void {
        if (this.statusHeartbeatInterval) {
            clearInterval(this.statusHeartbeatInterval);
        }

        this.statusHeartbeatInterval = setInterval(() => {
            this.sendStatusHeartbeat();
        }, this.STATUS_HEARTBEAT_INTERVAL_MS);
    }

    private sendStatusHeartbeat(): void {
        this.makeSureLocalStorageValuesAreSet();

        if (!this.sessionId || !this.currentCalcSession?.status) {
            console.log('[waStateManager] [SessionStatusAutomaton] (sendStatusHeartbeat) No session ID or status, skipping heartbeat');
            return;
        }

        console.log('[waStateManager] [SessionStatusAutomaton] (sendStatusHeartbeat) Sending periodic status heartbeat');

        // Update lastSeen and force send to server
        this.currentCalcSession.lastSeen = new Date().toISOString();
        this.updateSessionStatus(this.currentCalcSession, true);
    }

    /**
     * Reconcile is now event-driven, not a periodic poll: once on campaign-UI open (the
     * bulk-logs-view mount effect, which dispatches fetchCampaignStatsThunk directly) and once
     * here, on engine (re)connect. The real-time listener (RealTimeMessageAckHandler →
     * recordMessageDeliveryAck) is primary; this just catches up any acks that arrived while
     * WhatsApp was disconnected, for whichever campaign the user currently has open.
     *
     * Guards:
     *  • No-op unless a bulk-logs campaign view is open (CampaignReducer.campaignIdForLogsView).
     *  • DEBOUNCE: skip if we reconciled within CAMPAIGN_RECONCILE_DEBOUNCE_MS. A flapping WA
     *    connection would otherwise fire the full ack recalculation on every reconnect edge; this
     *    collapses a reconnect storm to at most one reconcile per window (mirrors
     *    MQTTSubscriber.resyncAfterGap's isWithinDebounce guard, same 15s window + same helper).
     *
     * SCOPE NOTE — the drip `sequence-detail.tsx` view is intentionally NOT covered here. It
     * dispatches the same fetchCampaignStatsThunk on-open for its step campaigns, but its
     * "open drip" id lives in the URL/route (SequenceDetail's `dripId` prop, sourced from the
     * router surface in campaigns-redesign-view.tsx) — it is NOT written to any Redux reducer this
     * content-script singleton can read. Rather than fabricate a store-visible "open drip" signal,
     * we leave sequence-detail out of the on-reconnect trigger; its on-open reconcile still keeps
     * it fresh. If a store-visible active-drip id is added later, reconcile it here too.
     *
     * Dynamically imports CampaignThunks to avoid pulling its module graph into every calcNewState
     * call (mirrors the MQTTSubscriber.resyncAfterGap pattern).
     */
    private reconcileOpenCampaignOnReconnect(): void {
        // `as any`: known type mismatch between the extension store and CommonRootState in this
        // directory (see real-time-message-ack-handler.ts).
        const state = store.getState() as any;
        const campaignIdForLogsView: string | undefined = state?.CampaignReducer?.campaignIdForLogsView;
        if (!campaignIdForLogsView) return;

        // Debounce reconnect storms (same time source + helper the MQTT gap-resync debounce uses).
        const now = Date.now();
        if (isWithinDebounce(this.lastCampaignReconcileAt, now, this.CAMPAIGN_RECONCILE_DEBOUNCE_MS)) {
            console.log('[waStateManager] [SessionStatusAutomaton] (reconcileOpenCampaignOnReconnect) debounced (reconciled moments ago) — skipping');
            return;
        }
        this.lastCampaignReconcileAt = now;

        console.log('[waStateManager] [SessionStatusAutomaton] (reconcileOpenCampaignOnReconnect) engine reconnected with an open campaign view — reconciling stats:', campaignIdForLogsView);
        void (async () => {
            try {
                const { fetchCampaignStatsThunk } = await import('@common-reducers/CampaignThunks');
                // `appDispatch` (the AppThunkDispatch-typed export) not `store.dispatch` — the store's
                // own dispatch is typed against this extension's reducer subset, which is missing
                // several CommonRootState reducers and fails to satisfy fetchCampaignStatsThunk's
                // `{ state: CommonRootState }` generic constraint.
                appDispatch(fetchCampaignStatsThunk(campaignIdForLogsView));
            } catch (e) {
                console.warn('[waStateManager] [SessionStatusAutomaton] (reconcileOpenCampaignOnReconnect) dispatch failed', (e as Error)?.message);
            }
        })();
    }

    /**
     * On engine (re)connect, catch up any drip reply-exits that were missed while
     * the engine was offline. Companion to the real-time onMessage → registerDripReply
     * path (WhatsappThunks): while the engine is down no onMessage fires, so a
     * recipient who replied never exits their stop-on-reply drip. Here we ask the
     * backend for ALL of the owner's active stop-on-reply drip targets and re-scan
     * each target chat's recent WA-store messages for an inbound reply in the target's
     * window; matches are exited via the same registerDripReply action.
     *
     * Attaches to the SAME reconnect edge as reconcileOpenCampaignOnReconnect but with
     * its OWN debounce field (DRIP_REPLY_RECONCILE_DEBOUNCE_MS), so a reconnect storm
     * collapses to at most one scan per window. Fire-and-forget — never blocks or throws
     * into the state machine. Idempotent (the backend dedupes already-exited recipients),
     * so over-firing is safe.
     */
    private reconcileDripRepliesOnReconnect(): void {
        // Debounce reconnect storms (same time source + helper as the campaign reconcile).
        const now = Date.now();
        if (isWithinDebounce(this.lastDripReplyReconcileAt, now, this.DRIP_REPLY_RECONCILE_DEBOUNCE_MS)) {
            console.log('[waStateManager] [SessionStatusAutomaton] (reconcileDripRepliesOnReconnect) debounced (reconciled moments ago) — skipping');
            return;
        }
        this.lastDripReplyReconcileAt = now;

        console.log('[waStateManager] [SessionStatusAutomaton] (reconcileDripRepliesOnReconnect) engine reconnected — scanning for missed drip reply-exits');
        void (async () => {
            try {
                // `appDispatch` (the AppThunkDispatch-typed export) not `store.dispatch` — see
                // reconcileOpenCampaignOnReconnect for why the store's own dispatch is too narrow.
                // forceRefetch + subscribe:false: this fires once per reconnect, not from a
                // subscribed component. Without forceRefetch the RTK cache pins the FIRST
                // fetch's snapshot for the whole session (a long-running gateway reconnects
                // for days), so a drip launched later would never appear in the target set —
                // exactly the miss this catch-up exists to prevent. subscribe:false avoids
                // leaking a subscriber per reconnect (the campaign path uses a fresh thunk).
                const targets = await appDispatch(
                    backendApi.endpoints.getActiveDripReplyTargets.initiate(undefined, {
                        forceRefetch: true,
                        subscribe: false,
                    }),
                ).unwrap();

                if (!targets || targets.length === 0) {
                    console.log('[waStateManager] [SessionStatusAutomaton] (reconcileDripRepliesOnReconnect) no active drip reply targets — nothing to scan');
                    return;
                }

                // Read each target chat's recent messages from the WA store and project to
                // { fromMe, t } with t normalized to epoch ms (the store's `t` is unix seconds).
                const messagesByChat: Record<string, ScanMsg[]> = {};
                for (const target of targets) {
                    const queryResult = await ContentMessageServiceActions.waQueryMessagesTool({
                        chatId: target.whatsappId,
                        limit: 50,
                        mode: 'latest',
                    });
                    const messages = unwrapQueryMessages(queryResult) ?? [];
                    messagesByChat[target.whatsappId] = messages.map((m) => ({
                        fromMe: !!m.fromMe,
                        t: (m.t ?? 0) * 1000,
                        type: m.type,
                        body: m.body,
                        waType: m.waType,
                        caption: m.caption ?? undefined,
                        hasMedia: m.hasAttachedMedia,
                    }));
                }

                const ids = collectRepliedTargets(targets, messagesByChat);
                console.log(`[waStateManager] [SessionStatusAutomaton] (reconcileDripRepliesOnReconnect) scanned ${targets.length} target(s) — ${ids.length} replied while offline`);
                if (ids.length > 0) {
                    // Catch-up scan has no per-message body (it only knows a reply
                    // landed while offline), so no `reply` snapshot here.
                    appDispatch(backendApi.endpoints.registerDripReply.initiate({ whatsappIds: ids }));
                }
            } catch (e) {
                console.warn('[waStateManager] [SessionStatusAutomaton] (reconcileDripRepliesOnReconnect) scan failed', (e as Error)?.message);
            }
        })();
    }

    public static getInstance(): SessionStatusAutomaton {
        if (!SessionStatusAutomaton.instance) {
            SessionStatusAutomaton.instance = new SessionStatusAutomaton();
        }
        return SessionStatusAutomaton.instance;
    }

    public calcNewState({ state, stream, hasSynced, qrcode, pairingCode, pairingCodeError, pairingMode }: WaStateStatus & { pairingCodeError?: string; pairingMode?: boolean }, triggerdBy = "", forceUpdate = false): void {

        this.makeSureLocalStorageValuesAreSet();
        // if (this.waLogoutTrigger) {
        //     console.log('[waStateManager] [SessionStatusAutomaton] (calcNewState): waLogoutTrigger: true - skipping');
        //     return
        // }

        // if (!this.sessionId) {
        //     return;
        // }

        console.log(`[waStateManager] [SessionStatusAutomaton] (calcNewState): triggerdBy: ${triggerdBy} | state: ${state} | stream: ${stream} | hasSynced: ${hasSynced} | qrcode: ${qrcode} | sessionId: ${this.sessionId} | forceUpdate: ${forceUpdate}`);

        // this.sessionHistoryLog.push({ ...this.currentCalcSession });
        // if (this.sessionHistoryLog.length > this.MAX_HISTORY) {
        //     this.sessionHistoryLog.shift();
        // }

        // const sessionFromDb = selectCurrentUserSession(store.getState());
        // if (!sessionFromDb?._id) {
        //     return;
        // }

        this.previusCalcSession = { ...this.currentCalcSession };

        this.currentCalcSession = {
            //...sessionFromDb,
            _id: this.sessionId,
            lastSeen: new Date().toISOString(),
            // Don't include qrcode field - let DB maintain it unless explicitly updating.
            // Same for rawWaStatus.qrcode: non-QR updates (handleWaStateHealthCheck /
            // appStateChanged) arrive WITHOUT a qrcode param — previously `qrcode || ""`
            // blanked rawWaStatus.qrcode to "" between QR rotations, making the UI QR
            // flicker/disappear. Preserve the last QR unless a branch below updates it
            // (handleQRChanged) or clears it (connected/logout).
            rawWaStatus: { state: state || "", stream: stream || "", hasSynced: hasSynced || false, qrcode: qrcode || (this.previusCalcSession?.rawWaStatus?.qrcode ?? "") },
            triggerdBy: triggerdBy || "",
            podId: this.podId,
            options: {
                ignoreOriginalEntity: true,
            }
        };


        if (triggerdBy === "handleWaLogout") {
            this.currentCalcSession.status = 'logout';
            this.currentCalcSession.qrcode = ''; // Clear QR code on logout
            this.currentCalcSession.rawWaStatus!.qrcode = ''; // ...and the mirrored rawWaStatus QR
            this.waLogoutTrigger = true;
            ContentMessageServiceActions.waTriggerLogoutTool();

        }


        else if (triggerdBy === "handleQRChanged" && qrcode && pairingMode) {
            // Pairing-code linking owns the screen: WhatsApp still rotates QR refs (and must, or
            // the companion hello has no ref), but neither the 'qrcode' status nor the QR timeout
            // applies — the user is entering a code, not scanning.
            this.currentCalcSession.status = this.previusCalcSession?.status || 'initializing';
        }


        else if (triggerdBy === "handleQRChanged" && qrcode) {
            //state: 'UNPAIRED', stream: 'DISCONNECTED', hasSynced: undefined
            if (!this.initialQRCodeTimestamp) {
                this.initialQRCodeTimestamp = Date.now();
            }
            console.log('[waStateManager] [SessionStatusAutomaton] (calcNewState): time left to trigger QR timeout :', (1 * 60 * 1000) - (Date.now() - this.initialQRCodeTimestamp));
            // #714: the timeout recycles an unscanned gateway pod. In a user's own browser it has no
            // job, and its page-side fallback (AppState.logout) reloads WhatsApp Web every ~4.5 min
            // while they sit on the QR or "link with phone number" screen. Checked at fire time:
            // the pod writes localStorage.sessionId after the first navigation.
            if (Date.now() - this.initialQRCodeTimestamp > this.QR_CODE_TIMEOUT_MS && getClientType() === ClientType.GATEWAY) {
                ContentMessageServiceActions.waTriggerQRTimeoutTool();
            }
            this.currentCalcSession.qrcode = qrcode;
            this.currentCalcSession.status = 'qrcode';
        }


        else if (triggerdBy === "handlePairingCode" && pairingCode) {
            // Pairing-code linking (gateway, linkMethod=pairing_code). Surface the
            // rotating code + PAIRING status the same way QR surfaces qrcode.
            this.currentCalcSession.pairingCode = pairingCode;
            this.currentCalcSession.pairingCodeError = '';
            this.currentCalcSession.status = 'PAIRING';
        }


        else if (triggerdBy === "handlePairingCode" && pairingCodeError) {
            // WhatsApp refused to issue a code (throttling, stale ref). Persist the reason so the
            // connect dialog can say so instead of spinning on "Requesting your code..." forever.
            this.currentCalcSession.pairingCodeError = pairingCodeError;
            this.currentCalcSession.pairingCodeErrorName = pairingCodeError;
            this.currentCalcSession.status = 'PAIRING';
        }


        else if (state === 'CONNECTED' && stream === 'CONNECTED') {
            // Edge-detect the transition INTO connected (as opposed to a re-affirmation of an
            // already-connected state) so the campaign-stats reconcile below fires once per
            // genuine (re)connect, not on every heartbeat.
            const isReconnectEdge = this.previusCalcSession?.status !== 'connected';
            this.initialQRCodeTimestamp = null;
            this.currentCalcSession.status = 'connected';
            this.currentCalcSession.wasConnected = true;
            this.currentCalcSession.qrcode = ''; // Explicitly clear QR code when connected
            this.currentCalcSession.rawWaStatus!.qrcode = ''; // ...and the mirrored rawWaStatus QR
            this.currentCalcSession.pairingCode = ''; // and the pairing code
            this.resetWaDisconnectTimer();
            if (isReconnectEdge) {
                this.reconcileOpenCampaignOnReconnect();
                this.reconcileDripRepliesOnReconnect();
                // Arm the WhatsApp account-health probe (restriction + send quota). Mounted
                // here for the connect edge only — the reporter itself is session-independent
                // and idempotent, so it covers plain extension users too, who never reach
                // updateSessionStatus below (no sessionId ⇒ early return).
                AccountHealthReporter.getInstance().onConnected();
            }
        }
        else if (state === 'OPENING') {
            this.currentCalcSession.status = 'OPENING';
        }
        else if (state === 'PAIRING') {
            this.currentCalcSession.status = 'PAIRING';
        }
        else if (!hasSynced && state === 'CONNECTED' && stream === 'DISCONNECTED') {
            console.log('[waStateManager] [SessionStatusAutomaton] (calcNewState): WhatsApp has not synced yet');
            this.initialQRCodeTimestamp = null;
            this.currentCalcSession.status = 'syncing';
            this.resetWaDisconnectTimer();

        }
        else if (hasSynced && state === 'CONNECTED' && stream === 'DISCONNECTED') {
            console.log('[waStateManager] [SessionStatusAutomaton] (calcNewState): WhatsApp synced but stream disconnected');
            this.currentCalcSession.status = 'stream_disconnected';
        }
        else {
            // For unhandled states, preserve previous status but still update lastSeen to prevent cleanup job
            console.log('[waStateManager] [SessionStatusAutomaton] (calcNewState): unhandled state:', state, stream, hasSynced, qrcode);
            this.currentCalcSession.status = this.previusCalcSession?.status || 'initializing';
        }

        this.publishWaPairing(deriveWaPairing({ state, triggerdBy, qrcode }));

        this.updateSessionStatus(this.currentCalcSession, forceUpdate);
    }

    /**
     * Tell the other clients (the web app's header indicator) whether this WhatsApp Web is linked.
     * Independent of the session doc: plain extension users have none, and the doc keeps its last
     * status on UNPAIRED updates that carry no QR. Republishes presence only on a change.
     */
    private publishWaPairing(pairing: WaPairing | undefined): void {
        if (!pairing || pairing === this.publishedWaPairing) return;
        this.publishedWaPairing = pairing;
        // Dynamic import, like the campaign reconcile: keeps the MQTT module graph out of every
        // calcNewState caller. Resolved imports run in call order, so updates can't reorder.
        void import("@common/noifier-module/MQTTSubscriber")
            .then(({ MQTTSubscriber }) => MQTTSubscriber.getInstance().setPresenceExtras({ waPairing: pairing }))
            .catch((error) => console.warn('[waStateManager] [SessionStatusAutomaton] (publishWaPairing) presence update failed', error));
    }

    private updateSessionStatus(session: Session, forceUpdate: boolean): void {

        //const sessionFromDb = selectCurrentUserSession(store.getState());
        // if its local - it will not have a session from db - therefore it will not do anything
        if (!session?._id) {
            console.log('[waStateManager] [SessionStatusAutomaton] (updateSessionStatus) Session ID not found, not updating');
            return;
        }

        if (this.previusCalcSession && !this.isDifferent(this.previusCalcSession, session) && !forceUpdate) {
            console.log('[waStateManager] [SessionStatusAutomaton] (updateSessionStatus) No difference detected and forceUpdate is false, not updating');
            return;
        }

        console.log('[waStateManager] [SessionStatusAutomaton] (updateSessionStatus) Updating session status. ForceUpdate:', forceUpdate);
        store.dispatch(DBSessionThunks.patch({
            entity: session,
            options: {
                // we need to ignore the original entity because the session may not be in the db yet
                ignoreOriginalEntity: true,
            }
        }));

    }

    private isDifferent(sessionFromDb: Session, currentCalcSession: Session): boolean {
        console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) Comparing session states');

        if (!sessionFromDb.rawWaStatus || !sessionFromDb.lastSeen || !currentCalcSession.lastSeen || !currentCalcSession.rawWaStatus) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) Missing required fields, returning true');
            return true;
        }

        if (sessionFromDb.status !== currentCalcSession.status) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) Status changed from', sessionFromDb.status, 'to', currentCalcSession.status);
            return true;
        }
        
        if (sessionFromDb.rawWaStatus.stream !== currentCalcSession.rawWaStatus.stream) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) Stream changed from', sessionFromDb.rawWaStatus.stream, 'to', currentCalcSession.rawWaStatus.stream);
            return true;
        }
        
        if (sessionFromDb.rawWaStatus.hasSynced !== currentCalcSession.rawWaStatus.hasSynced) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) hasSynced changed from', sessionFromDb.rawWaStatus.hasSynced, 'to', currentCalcSession.rawWaStatus.hasSynced);
            return true;
        }
        
        if (sessionFromDb.rawWaStatus.qrcode !== currentCalcSession.rawWaStatus.qrcode) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) QR code changed');
            return true;
        }

        // Convert string timestamps to Date objects for comparison
        const sessionFromDbLastSeenDate = new Date(sessionFromDb.lastSeen);
        const currentCalcSessionLastSeenDate = new Date(currentCalcSession.lastSeen);

        if (currentCalcSessionLastSeenDate.getTime() - sessionFromDbLastSeenDate.getTime() > this.LAST_SEEN_THRESHOLD) {
            console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) Last seen time difference exceeds threshold');
            return true;
        }

        console.log('[waStateManager] [SessionStatusAutomaton] (isDifferent) No differences found');
        return false;
    }

    public getWaStatus(): string {
        return this.currentCalcSession?.status || "";
    }


}